// PHA-567 SmartHub AI feed hook.
// Polls the org-scoped ranked list every 10 minutes (insights are nightly +
// on-demand, so frequent polling is wasteful). Always re-fetches on demand
// after a refresh or feedback action.
//
// The backend live-overlay sets `live_signal` on insights whose patient also
// appears in today's rule-based acute buckets, so the sort here trusts that
// signal — live insights always sort first, then risk_score desc.

import { useCallback, useEffect, useRef, useState } from 'react';
import { aiInsightsApi } from '../../../services/api';
import type { AiInsightsListResponse, PatientAiInsight } from '../types';

const POLL_MS = 10 * 60 * 1000;
// PHA-714: minimum gap between focus/visibility-triggered refetches so we
// don't hammer the backend on rapid tab-switches.
const SMARTHUB_AI_VIS_REFETCH_THROTTLE_MS = 10_000;

interface UseAiInsightsResult {
  enabled: boolean;
  insights: PatientAiInsight[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => Promise<void>;
}

const orderForBand = (a: PatientAiInsight, b: PatientAiInsight): number => {
  // Live alerts always sort to the top regardless of nightly risk_score.
  const liveA = a.live_signal ? 0 : 1;
  const liveB = b.live_signal ? 0 : 1;
  if (liveA !== liveB) return liveA - liveB;
  if (a.risk_score !== b.risk_score) return b.risk_score - a.risk_score;
  return a.computed_at < b.computed_at ? 1 : -1;
};

export function useAiInsights(organizationId?: string): UseAiInsightsResult {
  const [insights, setInsights] = useState<PatientAiInsight[]>([]);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  // PHA-714: tracks the timestamp of the last visibility/focus-triggered refetch.
  const lastVisibleRefetchRef = useRef<number>(0);

  const fetchOnce = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const data: AiInsightsListResponse = await aiInsightsApi.list(
        ctrl.signal,
        organizationId,
      );
      const list = (data?.insights || []).slice().sort(orderForBand);
      setInsights(list);
      setEnabled(Boolean(data?.enabled));
      setLastUpdated(new Date());
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setError((err as Error).message || 'Failed to load AI insights');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);

    // PHA-714: recover immediately when the tab regains focus instead of waiting
    // up to 10 minutes; a successful fetch clears the "feed unavailable" error.
    const onVisible = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastVisibleRefetchRef.current < SMARTHUB_AI_VIS_REFETCH_THROTTLE_MS) return;
      lastVisibleRefetchRef.current = now;
      fetchOnce();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      clearInterval(id);
      if (abortRef.current) abortRef.current.abort();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [fetchOnce]);

  return { enabled, insights, loading, error, lastUpdated, refetch: fetchOnce };
}
